import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { clsx } from "clsx";
import { useAuthStore } from "@/store/auth";
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  Bot,
  FileStack,
  LayoutGrid,
  LogOut,
  Mail,
  Menu,
  ScreenShare,
  Sun,
} from "lucide-react";
import { NotificationBell } from "@/features/tasks/components/NotificationBell";
import { WeatherIcon } from "@/components/icons/WeatherIcon";
import { useChatBadgeCounts } from "@/features/chat/api/chatApi";
import { useChatGlobalLive } from "@/features/chat/hooks/useChatGlobalLive";
import { useNavBadgeCounts } from "@/hooks/useNavBadges";
import { getRoleLabel } from "@/utils/roleLabel";
import { suppressPostLoginCapture } from "@/utils/postLoginRedirect";
import { clearPrivateLocalState } from "@/utils/clearSession";
import { disconnectRealtime } from "@/features/chat/api/realtime";
import { useScreenShareUiStore } from "@/features/screenshare/store/screenshareStore";
import { canUseScreenShare } from "@/features/screenshare/eligibility";

interface TopBarProps {
  onMenuClick: () => void;
}

// One shortcut icon of the top bar. The same list feeds the inline row (lg+) and
// the "Skróty" dropdown below lg — see the comment at the shortcuts array.
interface Shortcut {
  key: string;
  label: string;
  icon: ReactNode;
  /** Route for a link shortcut; mutually exclusive with onClick. */
  to?: string;
  /** Action for a button shortcut (screen share opens a picker, not a route). */
  onClick?: () => void;
  /** Unread counter painted in the corner of the icon. */
  badge?: number;
  /** Tiny always-on corner caption ("GG", "2"). */
  corner?: string;
}

export function TopBar({ onMenuClick }: TopBarProps) {
  const { user, logout } = useAuthStore();
  const qc = useQueryClient();
  const openScreenSharePicker = useScreenShareUiStore((s) => s.setPickerOpen);
  const isInternal = user?.role !== "contractor";

  // DEV-only visual cue: hardcoded at build time via Vite's import.meta.env.DEV
  // (true under `vite dev` on localhost, false in the production build). Paints the
  // WHOLE top bar in the brand red so localhost reads as "not production" instantly
  // and subconsciously — a thin accent gets lost because red is already used all
  // over the UI; only a full-bar colour field gives that at-a-glance separation.
  // Text CSS vars are remapped to light so descendant icons/labels (which read
  // these vars) stay legible on red. The app is light-only (index.css:
  // color-scheme: only light), so a fixed treatment is safe — no dark theme.
  const isDev = import.meta.env.DEV;
  const devBarStyle: CSSProperties | undefined = isDev
    ? ({
        "--color-text-primary": "#ffffff",
        "--color-text-secondary": "rgba(255,255,255,0.9)",
        "--color-text-muted": "rgba(255,255,255,0.7)",
      } as CSSProperties)
    : undefined;

  // GG (chat) badge — total unread messages across all rooms. Chat stays on its
  // own Centrifuge WebSocket (live); useChatGlobalLive refreshes the badge/room
  // list and plays the notification sound when a message lands in a closed room.
  // Warunek to `has_chat_permission`, nie `isInternal`: podwykonawca wpuszczony do
  // GG ma dostawać odznakę i dźwięk tak samo, inaczej wiadomości do niego przychodzą
  // w ciszy i nikt się do niego nie dowoła.
  const canUseChat = !!user?.has_chat_permission;
  const { data: chatBadge = { rooms: 0, messages: 0 } } =
    useChatBadgeCounts(canUseChat);
  useChatGlobalLive(canUseChat);

  // DLC_02 correspondence shortcut — envelope icon + "do odebrania" badge, next to
  // GG. Cache-only reader (the Sidebar's useNavBadges owns the single fetch); the
  // count mirrors the sidebar "Moja korespondencja" badge (correspondence.to_acknowledge).
  const canSeeDLC02 =
    (user?.accessible_dlc_modules ?? []).includes("dlc_02");
  const navBadges = useNavBadgeCounts();
  const correspondenceToAck = navBadges.correspondence.to_acknowledge;

  // DLC_03 ARON shortcut — ta sama bramka co grupa „ARON" w menu bocznym
  // (AronDockHost/AronFloatHost też pilnują dlc_03). Odznaka sumuje wszystko, co
  // czeka na KLIK użytkownika: propozycje zapisu z czatu i z poczty + nieodczytane
  // wyniki zadań cyklicznych — inaczej zatwierdzenie potrafi wisieć niezauważone.
  const canSeeAron =
    isInternal && (user?.accessible_dlc_modules ?? []).includes("dlc_03");
  // DLC_12 — Narzędzia PDF; moduł nadaje CEO, dyrektorzy techniczni mają go z urzędu.
  const canSeePdfTools =
    (user?.accessible_dlc_modules ?? []).includes("dlc_12");
  const aronPending =
    navBadges.aron_pending_writes.chat +
    navBadges.aron_pending_writes.mail +
    navBadges.aron_scheduled_unread;

  // Count badges default to a red fill, which vanishes on the red dev bar — invert
  // to white-fill/red-text there so the number stays legible.
  const badgeClass = clsx(
    "absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] text-[9px] flex items-center justify-center font-bold px-0.5 leading-none font-mono",
    isDev
      ? "bg-white text-[var(--color-primary)]"
      : "bg-[var(--color-primary)] text-white",
  );

  // Active nav icons default to the primary red — invisible on the red dev bar.
  // On dev the active state becomes full white (brighter than the 70%-white
  // inactive icons) so "you are here" still reads; on prod it stays red.
  const navIconClass = ({ isActive }: { isActive: boolean }) =>
    clsx(
      "relative p-1.5 transition-colors duration-100",
      isActive
        ? isDev
          ? "text-white"
          : "text-[var(--color-primary)]"
        : "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]",
    );

  const handleLogout = () => {
    // End any live screen share BEFORE tearing down auth/realtime, so the peer
    // gets a clean `end` while the request is still authenticated.
    useScreenShareUiStore.getState().endActive?.();
    disconnectRealtime();
    // Signing out on purpose is not an interrupted journey — don't come back here.
    suppressPostLoginCapture();
    logout({ deliberate: true });
    qc.clear();
    // Drafty, snapshot liczników i pogoda nie mogą zostać dla następnej osoby
    // przy tym samym komputerze (obejmuje też elg5-notif-leader-ts).
    clearPrivateLocalState();
  };

  // Shortcut icons, in bar order. A user with every module gets seven of them and
  // together with the bell, the avatar and "Wyloguj" they no longer fit a phone
  // screen (zgłoszenie „Widok mobilny"), so below lg the whole list moves into a
  // single "Skróty" dropdown; the bell, the avatar and logout stay on the bar.
  const shortcuts: Shortcut[] = [
    ...(user
      ? [
          {
            key: "bg-tasks",
            label: "Zadania w tle",
            to: "/bg-tasks",
            icon: <Activity size={18} />,
          },
        ]
      : []),
    ...(canUseScreenShare(user)
      ? [
          {
            key: "screenshare",
            label: "Podgląd ekranu",
            onClick: () => openScreenSharePicker(true),
            icon: <ScreenShare size={18} />,
          },
        ]
      : []),
    // Pracownicy GK zawsze; konto podwykonawcy tylko z flagą od CEO —
    // ta sama reguła co ChatGuard na trasie i HasChatAccess na backendzie.
    ...(canUseChat
      ? [
          {
            key: "chat",
            label: "GG",
            to: "/chat",
            icon: <Sun size={18} />,
            corner: "GG",
            badge: chatBadge.messages,
          },
        ]
      : []),
    ...(canSeeDLC02
      ? [
          {
            key: "correspondence",
            label: "Moja korespondencja",
            to: "/paper-send/mine",
            icon: <Mail size={18} />,
            badge: correspondenceToAck,
          },
        ]
      : []),
    // DLC_12 — narzędzia PDF. Zgłoszenie właściciela: „jakby była ikonka koło
    // GG i innych to by było mocno intuicyjne”. Moduł z panelu CEO.
    ...(canSeePdfTools
      ? [
          {
            key: "pdf",
            label: "Narzędzia PDF",
            to: "/pdf",
            icon: <FileStack size={18} />,
          },
        ]
      : []),
    ...(canSeeAron
      ? [
          {
            key: "aron",
            label: "Asystent ARON",
            to: "/aron",
            icon: <Bot size={18} />,
            badge: aronPending,
          },
        ]
      : []),
    // Jak przy GG: warunkiem jest `has_two_drive_permission`, nie `isInternal` —
    // podwykonawca wpuszczony na dysk przez CEO musi mieć czym na niego wejść.
    ...(user?.has_two_drive_permission
      ? [
          {
            key: "two-drive",
            label: "TwoDrive",
            to: "/two-drive",
            icon: <WeatherIcon size={18} />,
            corner: "2",
          },
        ]
      : []),
  ];

  const cornerClass =
    "absolute -top-0.5 -right-0.5 text-[9px] font-bold leading-none font-mono select-none pointer-events-none uppercase tracking-wider";

  const renderShortcutIcon = (s: Shortcut) => (
    <>
      {s.icon}
      {s.corner && <span className={cornerClass}>{s.corner}</span>}
      {!!s.badge && s.badge > 0 && (
        <span className={badgeClass}>{s.badge > 9 ? "9+" : s.badge}</span>
      )}
    </>
  );

  return (
    <header
      className={clsx(
        "h-14 flex items-center justify-between px-4 lg:px-6 gap-3 flex-shrink-0 border-b",
        isDev
          ? "bg-[var(--color-primary)] border-[var(--color-primary-hover)]"
          : "bg-[var(--color-bg-surface)] border-[var(--color-border)]",
      )}
      style={devBarStyle}
    >
      <button
        onClick={onMenuClick}
        className="lg:hidden p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-100 flex-shrink-0"
        aria-label="Otwórz menu"
      >
        <Menu size={20} />
      </button>

      {isDev && (
        <div
          className="hidden lg:flex items-center gap-2 select-none"
          title="Środowisko deweloperskie (localhost)"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping bg-white opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 bg-white" />
          </span>
          <span className="font-mono text-[11px] font-bold uppercase tracking-wider bg-white text-[var(--color-primary)] px-1.5 py-0.5 leading-none">
            Dev
          </span>
          <span className="font-mono text-[11px] uppercase tracking-wider text-white/80">
            localhost
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 lg:gap-4 ml-auto min-w-0">
        {/* Below lg the same list lives behind the "Skróty" button. */}
        <div className="hidden lg:flex items-center gap-4">
          {shortcuts.map((s) =>
            s.to ? (
              <NavLink
                key={s.key}
                to={s.to}
                title={s.label}
                aria-label={s.label}
                className={navIconClass}
              >
                {renderShortcutIcon(s)}
              </NavLink>
            ) : (
              <button
                key={s.key}
                onClick={s.onClick}
                title={s.label}
                aria-label={s.label}
                className="relative p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-100"
              >
                {renderShortcutIcon(s)}
              </button>
            ),
          )}
        </div>

        <ShortcutsMenu
          shortcuts={shortcuts}
          badgeClass={badgeClass}
          cornerClass={cornerClass}
          isDev={isDev}
        />

        <NotificationBell />
        {user && (
          <Link
            to="/settings"
            className="flex items-center gap-2 min-w-0 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-100"
          >
            {user.profile_picture_url ? (
              <img
                src={user.profile_picture_url}
                alt="Avatar"
                className="w-7 h-7 flex-shrink-0 object-cover border border-[var(--color-border)]"
              />
            ) : (
              <div className="w-7 h-7 flex-shrink-0 bg-[var(--color-primary)] flex items-center justify-center text-[var(--color-text-inverse)] text-xs font-mono font-bold select-none">
                {`${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() || "?"}
              </div>
            )}
            <span className="font-mono text-xs font-medium hidden sm:inline truncate uppercase tracking-wider">
              {user.first_name} {user.last_name}
            </span>
            <span className="text-[var(--color-text-muted)] hidden lg:inline">·</span>
            <span className="font-mono text-xs text-[var(--color-text-muted)] hidden lg:inline uppercase tracking-wider">
              {getRoleLabel(user.role, user.is_ceo)}
            </span>
            {user.branch?.name && (
              <>
                <span className="text-[var(--color-text-muted)] hidden lg:inline">·</span>
                <span className="font-mono text-xs text-[var(--color-text-muted)] hidden lg:inline uppercase tracking-wider">
                  {user.branch.name}
                </span>
              </>
            )}
            {user.has_orderer_permission && user.role === "employee" && (
              <span className="font-mono text-xs text-[var(--color-primary)] border border-[var(--color-primary)] px-1.5 py-0.5 uppercase tracking-wider hidden lg:inline">
                Zamawiający
              </span>
            )}
          </Link>
        )}
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 flex-shrink-0 text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-100"
          aria-label="Wyloguj"
          title="Wyloguj"
        >
          <LogOut size={14} />
          <span className="hidden sm:inline">Wyloguj</span>
        </button>
      </div>
    </header>
  );
}

/**
 * Below lg the shortcut icons don't fit next to the hamburger, the bell, the avatar
 * and "Wyloguj" — they collapse into this single button. The button carries the sum
 * of the hidden badges, so an unread GG message or a pending ARON write still shows
 * on a phone; the panel then lists every shortcut with its own label and badge.
 */
function ShortcutsMenu({
  shortcuts,
  badgeClass,
  cornerClass,
  isDev,
}: {
  shortcuts: Shortcut[];
  badgeClass: string;
  cornerClass: string;
  isDev: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (shortcuts.length === 0) return null;

  const total = shortcuts.reduce((sum, s) => sum + (s.badge ?? 0), 0);

  return (
    <div className="lg:hidden relative flex-shrink-0" ref={rootRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors duration-100"
        aria-label="Skróty"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Skróty"
      >
        <LayoutGrid size={18} />
        {total > 0 && (
          <span className={badgeClass}>{total > 9 ? "9+" : total}</span>
        )}
      </button>

      {open && (
        <>
          {/* Tap anywhere else closes — the panel itself sits above this layer. */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            // On the dev top bar the header remaps --color-text-* to white for
            // legibility on red; those vars cascade into this panel (a DOM child
            // of the bar) and would render its labels white on a light panel.
            // Reset them to the :root defaults so the menu always reads normally.
            style={
              isDev
                ? ({
                    "--color-text-primary": "#0a0a0a",
                    "--color-text-secondary": "#404040",
                    "--color-text-muted": "#888888",
                  } as CSSProperties)
                : undefined
            }
            className="absolute right-0 top-full mt-2 z-50 min-w-[220px] border border-[var(--color-border)] bg-[var(--color-bg-surface)] shadow-lg py-1"
            role="menu"
          >
            {shortcuts.map((s) => {
              const body = (
                <>
                  <span className="relative flex items-center justify-center w-[18px] h-[18px] text-[var(--color-text-muted)]">
                    {s.icon}
                    {s.corner && <span className={cornerClass}>{s.corner}</span>}
                  </span>
                  <span className="font-mono text-xs uppercase tracking-wider truncate">
                    {s.label}
                  </span>
                  {!!s.badge && s.badge > 0 && (
                    <span className="ml-auto min-w-[15px] h-[15px] text-[9px] flex items-center justify-center font-bold px-0.5 leading-none font-mono bg-[var(--color-primary)] text-white">
                      {s.badge > 9 ? "9+" : s.badge}
                    </span>
                  )}
                </>
              );
              const rowClass =
                "w-full flex items-center gap-3 px-3 py-2.5 text-left text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] transition-colors duration-100";
              return s.to ? (
                <NavLink
                  key={s.key}
                  to={s.to}
                  role="menuitem"
                  className={rowClass}
                  onClick={() => setOpen(false)}
                >
                  {body}
                </NavLink>
              ) : (
                <button
                  key={s.key}
                  role="menuitem"
                  className={rowClass}
                  onClick={() => {
                    setOpen(false);
                    s.onClick?.();
                  }}
                >
                  {body}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
